import { Avatar, Progress, message } from 'antd'
import axios from 'axios'
import { callMe, useAuth } from 'components'
import { GoogleSignInButton } from 'components/GoogleSignInButton/GoogleSignInButton'
import useWbsoApplicationById from 'data/wbsoApp/useWbsoApplicationById'
import useWbsoApplicationResponseById from 'data/wbsoApp/useWbsoApplicationResponseById'
import { useFormik } from 'formik'
import moment from 'moment'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { ApiCall } from 'services'
import { trackEvent } from 'utils/analytics'
import { PAGE } from 'utils/pages'
import { createFormValidationSchema } from '../schema/formValidationSchema'
import Button from './Button'
import Intro from './Intro'
import QuestionView from './QuestionView'
import SearchKVKForm from './SearchKVKForm'
import { numberWithCommas } from './numberWithCommas'

const ApplicationForm = ({
  loading = false,
  withIntro = false,
  id,
  currentPage,
  initialValues: initialFormValues,
  questions = [],
  feedbacks = [],
  props,
}) => {
  const LOGIN_REQUIRED_AFTER_QUESTION_INDEX = 3
  const router = useRouter()
  const [messageApi, contextHolder] = message.useMessage()

  const { signInWithGoogleAccount, authUser } = useAuth()

  const [loginNow, setLoginNow] = useState(false)

  const isAuthenticated = !!authUser

  const [showIntro, setShowIntro] = useState(withIntro)
  const [startingApp, setStartingApp] = useState(false)

  const [hasCompany, setHasCompany] = useState(authUser?.company !== null)

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [companyData, setCompanyData] = useState(authUser?.company ?? null)
  const [isNextDisabled, setIsNextDisabled] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmiting, setIsSubmiting] = useState(false)
  const [applicationId, setApplicationId] = useState()
  const [claimValue, setClaimValue] = useState(null)

  const {
    data: responses,
    refetch: refetchResponse,
    // isLoading: loadingResponse,
  } = useWbsoApplicationResponseById({
    applicationId: router.query.id ?? applicationId,
  })
  useEffect(() => {}, [authUser])

  const {
    data: application,
    // isLoading: loadingApplication,
    refetch: refetchApplication,
  } = useWbsoApplicationById({
    applicationId: router.query.id ?? applicationId,
  })

  useEffect(() => {
    if (application) {
      setClaimValue(application.value)
    }
  }, [application])

  const prompts = questions
    ?.sort((a, b) => a.order - b.order)
    .map((v) => {
      return {
        ...v,
        type: v.question_type,
        minLength: v.min_length,
        maxLength: v.max_length,
        options: v.options ? v.options : null,
        condition: v.condition
          ? new Function('formData', `return ${v.condition};`)
          : undefined,
      }
    })

  const initialValues = prompts.reduce((acc, question) => {
    let value =
      question.key === 'phase_description'
        ? [{ milestone: '', timeline: '' }]
        : ''

    if (initialFormValues) {
      const questionValue = initialFormValues?.find(
        (v) => v.question === question.uuid,
      )

      if (questionValue) {
        if (question.key === 'phase_description') {
          value = JSON.parse(questionValue.response)
        } else {
          value = questionValue.response
        }
      }
    }

    acc[question.key] = value
    return acc
  }, {})

  const validationSchema = createFormValidationSchema(prompts, initialValues)

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        if (feedbacks) {
          // Do something if it is feedback page
        } else if (initialFormValues) {
          // Do something if it is edit page
        } else {
          // Do something if it is create application page
        }

        router.push(PAGE.wbsoAppDashboard)
        messageApi.open({
          type: 'success',
          content: 'Thank you for your application',
        })
      } catch (error) {
        messageApi.open({
          type: 'error',
          content: 'Failed to save application',
        })
      } finally {
      }
    },
  })

  const visibleQuestions = useMemo(() => {
    const updatedVisibleQuestions = prompts.filter((question) => {
      if (question.condition && currentPage !== 'feedback') {
        return question.condition(formik.values)
      }
      return true
    })

    return updatedVisibleQuestions
  }, [formik.values, prompts])

  if (currentPage === 'feedback' && visibleQuestions.length === 0) {
    router.push(PAGE.wbsoAppDashboard)
  }

  const currentQuestion = visibleQuestions[currentQuestionIndex]

  const currentResponse = responses?.find(
    (v) => v.question === currentQuestion?.uuid,
  )

  const currentFeedback = currentResponse?.feedback

  useEffect(() => {
    const updatedValidationSchema = createFormValidationSchema(
      prompts,
      formik.values,
    )
    formik.setErrors({})
    formik.validateForm(updatedValidationSchema)
  }, [formik.values])

  useEffect(() => {
    if (currentQuestion) {
      formik.validateForm().then((errors) => {
        const hasErrors = errors[currentQuestion?.key]
        setIsNextDisabled(!!hasErrors)
      })
    }
    if (
      currentQuestionIndex >= LOGIN_REQUIRED_AFTER_QUESTION_INDEX &&
      !isAuthenticated
    ) {
      setLoginNow(true)
    }
  }, [currentQuestionIndex, formik.values])

  useEffect(() => {
    if (isAuthenticated) {
      formik.setFieldValue('authentication', true)
    }
  }, [isAuthenticated])

  const saveResponse = async (questionId, response) => {
    await ApiCall.wbso.saveApplicationResponse(applicationId ?? id, [
      {
        application: applicationId ?? id,
        question: questionId,
        response,
      },
    ])
  }

  const updateCompanyDescription = async (description) => {
    await ApiCall.Companies.update(authUser.company.uuid, {
      description,
    })
  }

  const updateResponse = async (responseId, questionId, response) => {
    await ApiCall.wbso.updateApplicationResponse(
      applicationId ?? id,
      responseId,
      {
        application: applicationId ?? id,
        question: questionId,
        response,
      },
    )
  }

  const uploadFile = async (file) => {
    const uploadRes = await ApiCall.wbso.uploadFile(applicationId ?? id, file)
    const newData = {}
    uploadRes.data.forEach((res) => {
      const questionKey = questions.find((q) => q.uuid === res.question)?.key
      if (questionKey) {
        newData[questionKey] = res.response
      }
    })

    formik.setValues({
      ...formik.values,
      ...newData,
    })
  }

  const handleNext = () => {
    formik.validateForm().then(async (errors) => {
      if (!errors[currentQuestion.key]) {
        if (!isAuthenticated) {
          setCurrentQuestionIndex(currentQuestionIndex + 1)
          return
        }

        const questionId = currentQuestion.uuid
        let value = formik.values[currentQuestion.key]
        let currentValue = currentResponse?.response
        if (currentQuestion.type === 'Date') {
          value = moment(value).format('DD-MM-YYYY')
          currentValue = currentValue
            ? moment(currentValue).format('DD-MM-YYYY')
            : undefined
        }
        if (currentQuestion.key === 'phase_description') {
          value = JSON.stringify(value)
        }

        try {
          setIsSubmiting(true)

          if (currentValue && currentValue !== value) {
            if (currentQuestion.key === 'company_description') {
              await updateCompanyDescription(value)
              await updateResponse(currentResponse?.uuid, questionId, value)
            } else if (currentQuestion.question_type === 'FileUpload') {
              await uploadFile(value)
            } else {
              await updateResponse(currentResponse?.uuid, questionId, value)
            }
          } else if (currentQuestion.key === 'company_description') {
            await updateCompanyDescription(value)
          } else if (currentQuestion.question_type === 'FileUpload') {
            await uploadFile(value)
          } else if (
            currentQuestionIndex >= 0 &&
            isAuthenticated &&
            !currentValue
          ) {
            await saveResponse(questionId, value)
          }

          refetchResponse()
          refetchApplication()

          if (currentQuestionIndex < visibleQuestions.length - 1) {
            if (currentPage === 'feedback') {
              setCurrentQuestionIndex(0)
            } else {
              setCurrentQuestionIndex(currentQuestionIndex + 1)
            }
          } else {
            formik.submitForm()
          }
        } catch (error) {
          messageApi.open({
            type: 'error',
            content:
              error.response.data[0]?.response?.[0] ??
              'Failed to save response',
          })
        } finally {
          setIsSubmiting(false)
        }
      } else {
        formik.setTouched({ [currentQuestion.key]: true })
      }
    })
  }

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const handleSelectCompany = async (company) => {
    if (isAuthenticated) {
      try {
        setIsLoading(true)

        trackEvent(
          'User selects company',
          { name: company.name, kvk: company.kvk_number },
          authUser?.uuid,
        )

        const { data: companyCreated } = await ApiCall.Companies.create({
          name: company.name,
          domain: company.domain,
          website: company.domain,
          kvk_number: company.kvk_number,
          products: ['APPLICATION'],
        })

        setCompanyData(company)
        setHasCompany(true)
        await createApplication(companyCreated.uuid)
      } catch (error) {
        console.error('Error selecting company:', error)
      } finally {
        setIsLoading(false)
      }
    } else {
      trackEvent(
        'User selects company',
        { name: company.name, kvk: company.kvk_number },
        authUser?.uuid,
      )
      setCompanyData(company)
      setHasCompany(true)
    }
  }

  const savePreAuthQuestion = async (applicationId, token) => {
    const values = prompts
      ?.slice(0, LOGIN_REQUIRED_AFTER_QUESTION_INDEX)
      ?.map((q) => {
        return {
          application: applicationId,
          question: q.uuid,
          response: formik.values[q.key],
        }
      })
    await ApiCall.wbso.saveApplicationResponse(applicationId, values)
  }

  const createApplication = async (companyId) => {
    try {
      const result = await ApiCall.wbso.createApplication(companyId)
      const appId = result.data.uuid
      setApplicationId(appId)
      return appId
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // If unauthorized, wait briefly and retry once
        await new Promise((resolve) => setTimeout(resolve, 1000))
        const retryResult = await ApiCall.wbso.createApplication(companyId)
        const retryAppId = retryResult.data.uuid
        setApplicationId(retryAppId)
        return retryAppId
      }
      throw error // If it's not an auth error or the retry fails, throw the error
    }
  }

  const handleAuth = async () => {
    try {
      const token = await signInWithGoogleAccount({ keepLoggedIn: false })
      const user = await callMe(token)

      if (user) {
        let companyId = user?.company?.uuid
        if (!companyId) {
          // ApiCall.Companies.create({})
          // TODO: CHECK IF WE CAN USE API CALL
          const result = await axios.post(
            `${process.env.NEXT_PUBLIC_BACKEND_API}/companies/`,
            {
              name: companyData.name,
              domain: companyData.domain,
              website: companyData.domain,
              kvk_number: companyData.kvk_number,
              products: ['APPLICATION'],
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          const user = await callMe(token)
          companyId = user?.company?.uuid
          if (!companyId) {
            throw Error('Error signing up user & company')
          }
        }

        if (companyId) {
          const appId = await createApplication(companyId)
          await savePreAuthQuestion(appId, token)
        }
      }
    } catch (error) {
      console.log('ERROR', error)
    } finally {
    }
  }

  const handleStartApp = async () => {
    try {
      if (authUser?.company) {
        setStartingApp(true)
        await createApplication(authUser.company.uuid)

        setHasCompany(true)
      } else {
        setHasCompany(false)
      }

      setShowIntro(false)
    } catch (error) {
      message.error('Failed to starting the application!')
    } finally {
      setStartingApp(false)
    }
  }

  if (showIntro) {
    return (
      <Intro
        loading={startingApp}
        onBack={() => router.push(PAGE.wbsoApplication)}
        onContinue={handleStartApp}
      />
    )
  }

  const currentStep = currentQuestionIndex + 1
  const totalStep = visibleQuestions.length

  return (
    <div className="flex flex-col py-8 gap-y-5 font-inter w-full max-w-[1240px] md:gap-y-16 md:py-16 lg:pb-32">
      {contextHolder}

      <div className="flex flex-col gap-y-2">
        <div className="flex items-center justify-between text-white">
          <span className="font-semibold">
            Step {hasCompany ? currentStep : 1}:{' '}
            {hasCompany ? currentQuestion.title : 'Select Company'}
          </span>
          <span className="opacity-60">
            {hasCompany ? totalStep - currentStep : totalStep + 1 - currentStep}{' '}
            questions remaining
          </span>
        </div>
        <Progress
          showInfo={false}
          className="h-2"
          percent={(currentStep * 100) / totalStep}
          trailColor="#FFFFFF4D"
          strokeColor="white"
        />
      </div>

      <div className="flex flex-col-reverse gap-y-4 gap-x-16 xl:flex-row">
        <div className="flex w-full min-h-[425px] flex-col gap-y-4 bg-white rounded-[20px] px-5 py-8 text-g400 xl:max-w-[760px] md:p-10">
          {!hasCompany ? (
            <div className="flex flex-col gap-y-3">
              <h2 className="text-2xl mb-4 font-semibold md:text-4xl">
                Select Your Company
              </h2>
              <SearchKVKForm onHandle={handleSelectCompany} />
            </div>
          ) : currentQuestionIndex >= LOGIN_REQUIRED_AFTER_QUESTION_INDEX &&
            !isAuthenticated ? (
            <div
              className="w-max flex flex-col gap-y-2"
              style={{ maxWidth: '100%', overflowWrap: 'break-word' }}
            >
              <h2 className="text-2xl font-semibold md:text-4xl">
                You're almost there!
              </h2>
              <p>
                Sign in with Google to continue and discover your potential WBSO
                value. By signing in, you'll save your progress and gain access
                to personalized insights about your subsidy opportunity.
              </p>
              <div className="w-max mt-8">
                <GoogleSignInButton
                  handleSignIn={handleAuth}
                  text={'Sign in with Google'}
                />
              </div>
            </div>
          ) : (
            <QuestionView formik={formik} currentQuestion={currentQuestion} />
          )}

          {hasCompany && (
            <div className="flex flex-wrap gap-x-8 gap-y-4 mt-auto">
              <Button
                variant="outlined"
                color="primary"
                className="w-full flex-1"
                onClick={handlePrev}
              >
                Previous step
              </Button>
              <Button
                variant="contained"
                color="primary"
                className="w-full flex-1"
                disabled={isNextDisabled}
                loading={isSubmiting}
                onClick={handleNext}
              >
                {totalStep - currentStep === 0 ? 'Submit' : 'Next'}
              </Button>
            </div>
          )}
        </div>
        <RightItem
          feedback={currentFeedback}
          claimValue={claimValue}
          loginNow={loginNow}
        />
      </div>
    </div>
  )
}

const FeedbackCard = ({ feedback, onClose }) => {
  return (
    <div className="relative z-[2] max-w-[525px] flex flex-col items-start px-6 pt-5 pb-8 bg-[#F9D549] rounded-3xl font-inter text-g400 lg:w-[525px]">
      {/* <button
        className="bg-transparent outline-none absolute right-4 top-4"
        onClick={onClose}
      >
        <CloseSquareOutlined className="text-2xl" />
      </button> */}

      <div className="font-semibold text-2xl">Feedback</div>
      <div
        className="feedback-content mt-2"
        dangerouslySetInnerHTML={{ __html: feedback.feedback_text }}
      />

      <style jsx global>{`
        .feedback-content ul {
          list-style-type: disc !important; /* Ensures bullet points */
          padding-left: 20px !important; /* Adds indentation for the list */
        }
        .feedback-content li {
          margin-bottom: 5px !important; /* Optional: adds space between items */
        }
      `}</style>
      <div className="flex items-center gap-x-3 mt-5">
        <Avatar className="w-11 h-11" />
        <div className="flex flex-col items-start">
          <span className="font-semibold">Anna</span>
          <span>Traqqie WBSO Expert</span>
        </div>
      </div>

      <svg
        width="42"
        height="46"
        viewBox="0 0 42 46"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute -right-0.5 -bottom-2"
      >
        <path
          d="M41.6292 45.0117L0 26.7152L35.1717 0L41.6292 45.0117Z"
          fill="#F9D549"
        />
      </svg>
    </div>
  )
}

const ClaimCard = ({ claimValue, loginNow = false }) => {
  const [randomValue, setRandomValue] = useState(
    claimValue ? claimValue : 52300,
  )

  useEffect(() => {
    if (loginNow && !claimValue) {
      const interval = setInterval(() => {
        const min = 25000 // minimum random value
        const max = 600000 // maximum random value
        const generatedValue = Math.floor(Math.random() * (max - min + 1)) + min
        setRandomValue(generatedValue)
      }, 2000)

      return () => clearInterval(interval) // Cleanup on unmount
    } else if (claimValue) {
      setRandomValue(claimValue)
    }
  }, [loginNow, claimValue])

  let textValue = ''
  let description = ''
  if (claimValue) {
    textValue = `€ ${numberWithCommas(claimValue)}`
    description = 'Your potential WBSO claim'
  } else {
    if (loginNow) {
      textValue = `€ ${numberWithCommas(randomValue)}`
      description = 'We have enough data! Login to see your claim!'
    } else {
      textValue = `€ ---.---`
      description = 'Answer more to see your claim'
    }
  }

  return (
    <div className="w-full max-w-[423px] h-max relative bg-g400 text-white flex flex-col items-start py-4 px-7 rounded-3xl md:px-12 md:py-8">
      <img
        src="/icons/wallet.svg"
        className="w-24 h-auto absolute left-0 top-0 opacity-50 md:w-44"
      ></img>

      <span className="text-[32px] font-semibold z-[2] md:text-6xl md:leading-normal">
        {textValue}
      </span>
      <span className="font-semibold z-[2]">{description}</span>
      <p className="opacity-70 mt-0.5 z-[2]">
        {claimValue ? `(based on information you’ve provided)` : ''}
      </p>
    </div>
  )
}

const RightItem = ({ feedback, claimValue, loginNow = false }) => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(true)

  return (
    <>
      <div className="lg:hidden">
        {isFeedbackOpen && feedback ? (
          <FeedbackCard
            feedback={feedback}
            onClose={() => setIsFeedbackOpen(false)}
          />
        ) : (
          <ClaimCard claimValue={claimValue} />
        )}
      </div>

      <div className="hidden relative flex-col gap-y-16 pt-4 lg:flex">
        <ClaimCard claimValue={claimValue} loginNow={loginNow} />

        {isFeedbackOpen && feedback && (
          <div className="absolute right-0 top-[280px]">
            <FeedbackCard
              feedback={feedback}
              onClose={() => setIsFeedbackOpen(false)}
            />
          </div>
        )}
        {/* <div className="hidden relative flex-col gap-y-7 items-start text-white xl:flex">
          <span className="text-2xl font-semibold">
            Complete all these steps to get a benefit
          </span>
          <ul className="flex flex-col gap-y-6">
            {[1, 2, 3].map((v) => {
              return (
                <li key={v} className="flex items-center gap-x-2">
                  <CheckCircleFilled className="text-2xl text-[#85FFD6]" />
                  <span className="">Lorem ipsum dolor sit {v}</span>
                </li>
              )
            })}
          </ul>
        </div> */}
      </div>
    </>
  )
}

export default ApplicationForm
